import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import LoginForm from './components/LoginForm';
import RegisterForm from './components/RegisterForm';
import ForgotPasswordForm from './components/ForgotPasswordForm';
import TrustSignals from './components/TrustSignals';
import BiometricAuth from './components/BiometricAuth';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';

const Authentication = () => {
  const [currentView, setCurrentView] = useState('login'); // login, register, forgot, biometric, success
  const [showBiometric, setShowBiometric] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Get redirect path from location state or default to dashboard
  const redirectPath = location?.state?.from || '/user-dashboard';

  useEffect(() => {
    // Check if user came from booking flow
    const urlParams = new URLSearchParams(location.search);
    const fromBooking = urlParams?.get('from') === 'booking';
    
    if (fromBooking) {
      // Show register form for new users from booking flow
      setCurrentView('register');
    }

    // Check for biometric support on component mount
    const checkBiometric = async () => {
      if (window.PublicKeyCredential && navigator.credentials) {
        // Check if user has previously set up biometric auth
        const hasBiometric = localStorage.getItem('biometric_enabled') === 'true';
        if (hasBiometric && currentView === 'login') {
          setShowBiometric(true);
        }
      }
    };

    checkBiometric();
  }, [location, currentView]);

  const handleAuthSuccess = () => {
    setIsLoading(true);
    
    // Simulate authentication process
    setTimeout(() => {
      // Store auth token (mock)
      localStorage.setItem('auth_token', 'mock_jwt_token_' + Date.now());
      localStorage.setItem('user_authenticated', 'true');
      
      // Navigate to intended destination
      navigate(redirectPath, { replace: true });
      setIsLoading(false);
    }, 1000);
  };

  const handleBiometricSuccess = () => {
    setCurrentView('success');
    setTimeout(() => {
      handleAuthSuccess();
    }, 1500);
  };

  const handleSwitchToLogin = () => {
    setCurrentView('login');
    setShowBiometric(false);
  };

  const handleSwitchToRegister = () => {
    setCurrentView('register');
    setShowBiometric(false);
  };

  const handleForgotPassword = () => {
    setCurrentView('forgot');
    setShowBiometric(false);
  };

  const handleForgotPasswordSuccess = () => {
    setCurrentView('success');
    setTimeout(() => {
      setCurrentView('login');
    }, 2000);
  };

  const handleSkipBiometric = () => {
    setShowBiometric(false);
  };

  const renderSuccessView = () => (
    <div className="w-full max-w-md mx-auto text-center">
      <div className="w-20 h-20 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
        <Icon name="CheckCircle" size={32} className="text-success" />
      </div>
      <h2 className="text-2xl font-bold text-foreground mb-2">
        {currentView === 'success' && location?.state?.from ? 'Welcome Back!' : 'Success!'}
      </h2>
      <p className="text-muted-foreground mb-6">
        {currentView === 'success' && location?.state?.from ?'Redirecting you to complete your booking...' :'Your password has been reset successfully. You can now sign in with your new password.'
        }
      </p>
      <div className="flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    </div>
  );

  const renderMainContent = () => {
    if (showBiometric) {
      return (
        <BiometricAuth
          onSuccess={handleBiometricSuccess}
          onSkip={handleSkipBiometric}
        />
      );
    }

    switch (currentView) {
      case 'register':
        return (
          <RegisterForm
            onSwitchToLogin={handleSwitchToLogin}
            onSuccess={handleAuthSuccess}
          />
        );
      case 'forgot':
        return (
          <ForgotPasswordForm
            onBackToLogin={handleSwitchToLogin}
            onSuccess={handleForgotPasswordSuccess}
          />
        );
      case 'success':
        return renderSuccessView();
      default:
        return (
          <LoginForm
            onSwitchToRegister={handleSwitchToRegister}
            onForgotPassword={handleForgotPassword}
            onSuccess={handleAuthSuccess}
          />
        );
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[calc(100vh-8rem)]">
          {/* Left Side - Form */}
          <div className="flex items-center justify-center">
            <div className="w-full max-w-md">
              {renderMainContent()}
            </div>
          </div>

          {/* Right Side - Trust Signals & Branding */}
          <div className="hidden lg:block">
            <div className="relative">
              {/* Hero Image */}
              <div className="relative h-96 rounded-2xl overflow-hidden mb-8">
                <Image
                  src="https://images.unsplash.com/photo-1488646953014-85cb44e25828?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                  alt="Travelers exploring beautiful destinations"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 text-white">
                  <h3 className="text-2xl font-bold mb-2">Discover Amazing Places</h3>
                  <p className="text-white/90">
                    Join millions of travelers exploring the world with TourEase
                  </p>
                </div>
              </div>

              {/* Trust Signals */}
              <TrustSignals />

              {/* Testimonial */}
              <div className="mt-8 p-6 bg-white rounded-xl border border-border">
                <div className="flex items-start space-x-4">
                  <Image
                    src="https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-4.0.3&auto=format&fit=crop&w=100&q=80"
                    alt="Sarah Johnson"
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div className="flex-1">
                    <div className="flex items-center space-x-1 mb-2">
                      {[...Array(5)]?.map((_, i) => (
                        <Icon key={i} name="Star" size={14} className="text-warning fill-current" />
                      ))}
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      "TourEase made planning our European adventure so easy. The booking process was seamless and the customer support was exceptional!"
                    </p>
                    <div className="text-xs">
                      <span className="font-medium text-foreground">Sarah Johnson</span>
                      <span className="text-muted-foreground"> • Verified Traveler</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Trust Signals */}
        <div className="lg:hidden mt-12">
          <TrustSignals />
        </div>
      </div>
      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 flex items-center space-x-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            <span className="text-foreground">Signing you in...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default Authentication;